import { DateTimeFormatter } from '@wix/premium-react-text';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@wix/design-system';
import { PlanType } from 'enums';
import { RootStore, withInjectedRootStore } from 'providers';
import s from './PageFooter.scss';
import classNames from 'classnames';
import { formatSaleEndDate } from 'utils';

interface SaleDisclaimerProps {
  rootStore: RootStore;
  dataHook?: string;
}

const SaleDisclaimerText: React.FC = ({ children }) => {
  return (
    <Text
      weight='thin'
      size='tiny'
      secondary
      className={s.saleDisclamer}
      dataHook='disclaimer-text'
    >
      {children}
    </Text>
  );
};

const SaleDisclaimerComp: React.FC<SaleDisclaimerProps> = ({
  rootStore,
  children,
  dataHook,
}) => {
  const {
    isInSeasonalSale,
    isInPersonalSale,
    appContext,
    planType,
    isInSale,
    currentStep,
    isInPermanentSale,
  } = rootStore.appStore;
  const {
    country,
    saleParams: { saleEndDate, ltoEndDate },
    isLimitedTimeDiscount,
  } = appContext;

  const { t } = useTranslation();
  const isInCheckoutPage = () => currentStep === 2;
  return (
    <div
      className={classNames(s.disclaimer, {
        [s.checkoutPageDisclaimer]: isInCheckoutPage(),
      })}
      data-hook={dataHook}
      data-testid='disclaimer'
    >
      {isLimitedTimeDiscount &&
        planType === PlanType.Business &&
        !isInSale &&
        ltoEndDate && (
          <DateTimeFormatter
            dateFormat='longMonth'
            render={(format) => (
              <SaleDisclaimerText>
                {t('Advanced.Plans_General_sale.footer.offer', {
                  date: format(ltoEndDate),
                })}
              </SaleDisclaimerText>
            )}
          />
        )}
      {isInPermanentSale && !isInCheckoutPage() && (
        <SaleDisclaimerText>
          {t('premium-purchase-plan.permanent_50.disclaimer')}
        </SaleDisclaimerText>
      )}
      {isInSeasonalSale && saleEndDate && (
        <DateTimeFormatter
          dateFormat='longMonth'
          render={(format) => (
            <SaleDisclaimerText>
              {t('sale.footer.offer', {
                date: formatSaleEndDate({
                  saleEndDate,
                  country,
                  formatter: format,
                }),
              })}
            </SaleDisclaimerText>
          )}
        />
      )}
      {isInPersonalSale && (
        <SaleDisclaimerText>
          {t('sale.footer.personal_sale_offer')}
        </SaleDisclaimerText>
      )}
      <SaleDisclaimerText>{children}</SaleDisclaimerText>
    </div>
  );
};

export const SaleDisclaimer = withInjectedRootStore(
  observer(SaleDisclaimerComp),
);
